<script setup>
import { computed } from 'vue'
import { useAssetTransforms } from '@/Composables'

const { assetUrl, marketingAssetUrl } = useAssetTransforms()

const props = defineProps({
    product: {
        type: Object,
        required: true
    },
    productImage: {
        type: String,
        required: false,
        default: null
    },
    vendorLogo: {
        type: String,
        required: false,
        default: null
    }
})

const productSchema = computed(() => {
    const sku = props.product.sku
    const vendorName = props.product.vendor?.display_name
    const vendorSlug = props.product.vendor?.slug

    const schema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": sku?.toUpperCase() || '',
        "description": props.product.descriptions?.['product-description']?.[0]?.short || '',
        "url": `https://www.seclock.com/catalog/products/${props.product.slug}`,
    }

    // Only add brand/manufacturer if vendor data exists
    if (vendorName) {
        schema.brand = {
            "@type": "Brand",
            "name": vendorName,
            ...(vendorSlug && { "@id": `https://www.seclock.com/catalog/manufacturers/${vendorSlug}` })
        }
        schema.manufacturer = {
            "@type": "Organization",
            "name": vendorName,
            ...(vendorSlug && { "@id": `https://www.seclock.com/catalog/manufacturers/${vendorSlug}` })
        }
    }

    // Product image - required for merchant listings
    const productImages = props.product.assets?.['product-image'] || []
    if (productImages.length > 1) {
        schema.image = productImages.map(img => assetUrl(img.external_id, 'product-detail'))
    } else if (productImages.length === 1) {
        schema.image = assetUrl(productImages[0].external_id, 'product-detail')
    } else if (props.vendorLogo) {
        schema.image = marketingAssetUrl(props.vendorLogo, 'meta-og-image-pad')
    } else {
        schema.image = marketingAssetUrl('seclock_og_default', 'meta-og-image')
    }

    // Offers with priceSpecification - required for merchant listings
    const listPrice = parseFloat(props.product.pricing_warehouse_item?.list_price)
    if (listPrice > 0) {
        schema.offers = {
            "@type": "Offer",
            "url": `https://www.seclock.com/catalog/products/${props.product.slug}`,
            "price": parseFloat(listPrice.toFixed(2)),
            "priceCurrency": "USD",
            "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": parseFloat(listPrice.toFixed(2)),
                "priceCurrency": "USD",
                "priceType": "https://schema.org/ListPrice"
            },
            "availability": "https://schema.org/InStock",
            "seller": {
                "@type": "Organization",
                "@id": "https://www.seclock.com/#organization",
                "name": "SECLOCK"
            },
            "shippingDetails": {
                "@type": "OfferShippingDetails",
                "shippingDestination": {
                    "@type": "DefinedRegion",
                    "addressCountry": "US"
                },
                "deliveryTime": {
                    "@type": "ShippingDeliveryTime",
                    "handlingTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 0,
                        "maxValue": 1,
                        "unitCode": "DAY"
                    },
                    "transitTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 1,
                        "maxValue": 5,
                        "unitCode": "DAY"
                    }
                }
            },
            "hasMerchantReturnPolicy": {
                "@type": "MerchantReturnPolicy",
                "applicableCountry": "US",
                "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                "merchantReturnDays": 30,
                "returnMethod": "https://schema.org/ReturnByMail",
                "returnFees": "https://schema.org/RestockingFees"
            }
        }
    }

    // Add product specifications as additionalProperty (product-specs + finish, not technical-specs)
    const productSpecIds = props.product.grouped_attributes?.['product-specs'] || []
    const finishIds = props.product.typed_attributes?.finish || []
    const specIds = [...new Set([...productSpecIds, ...finishIds])]

    if (specIds.length > 0 && props.product.attributes) {
        schema.additionalProperty = specIds
            .map(id => props.product.attributes[id])
            .filter(attr => attr?.value)
            .map(attr => ({
                "@type": "PropertyValue",
                "name": attr.type?.label || attr.description || attr.slug,
                "value": attr.value
            }))
    }

    // Add product categories from tags
    if (props.product.tags?.category) {
        schema.category = props.product.tags.category.map(tag => tag.name)
    }

    // Add series information (isVariantOf)
    if (props.product.series?.name) {
        const variantOf = {
            "@type": "ProductGroup",
            "name": props.product.series.name,
            "productGroupID": props.product.series.slug,
            "@id": `https://www.seclock.com/catalog/series/${props.product.series.slug}`
        }

        const seriesDescription = props.product.series?.descriptions?.['series-description']?.[0]?.short
        if (seriesDescription) {
            variantOf.description = seriesDescription
        }

        schema.isVariantOf = variantOf
    }

    // Add MPN only if product has a UPC code
    const upcAttr = props.product.attributes
        ? Object.values(props.product.attributes).find(attr => attr.type?.slug === 'upc-code')
        : null
    if (upcAttr?.value) {
        schema.mpn = upcAttr.value
    }

    return schema
})
</script>

<template>
    <component is="script" type="application/ld+json">
        {{ JSON.stringify(productSchema) }}
    </component>
</template>
